import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Tabs, Tab } from '@mui/material';
import { FaceRounded } from "@mui/icons-material";

import { validateToken } from '../../services/api';
import { useStoreByName } from '../../hooks/use-stores.hooks';
import { LoaderComponent } from '../components/core/LoaderComponent';
import BookAccordionRouter from '../components/accordions/BookAccordionRouter';
import ArrowRight from '../../assets/SVGComponents/iconArrowRight';
import { BOOK_LIST } from '../../constants/books.constants';
import type { IUserScoreInfo, IBook } from '@shared/types';
import { useBookAccordionData } from '../../hooks/useBookAccordionData';
import { useBookContent } from '../../hooks/useBooksQuery';

const PersonalArea: React.FC = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { getUserData, userData } = useStoreByName('userStore');
  const { uiStore } = useStoreByName('uiStore');
  const { selectedBook } = useStoreByName('booksStore');
  
  // TanStack Query hook for book content - handles caching automatically
  // This ensures data is loaded when accordions call setReadingContext
  useBookContent(selectedBook?.name_en || '');

  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
  const [isTeacher, setIsTeacher] = useState(false);
  const [studentData, setStudentData] = useState<IUserScoreInfo[] | null>(null);
  const [selectedBookName, setSelectedBookName] = useState('');

  // Books with available score data
  const booksWithScores: IUserScoreInfo[] = useMemo(() => 
    studentData || userData?.scoreInfo || [], 
    [studentData, userData?.scoreInfo]
  );

  // Custom hook for managing accordion data - handles all the complex logic
  const accordionItems = useBookAccordionData(
    booksWithScores,
    selectedBookName,
    isTeacher
  );

  // Load saved state from uiStore
  useEffect(() => {
    const loadSavedState = () => {
      if (!uiStore) return;
      
      const savedState = uiStore.restoreNavigationState();
      if (savedState) {
        // Check if the saved book still exists in available books
        if (savedState.selectedBook && booksWithScores.some(book => book.book_name_en === savedState.selectedBook)) {
          setSelectedBookName(savedState.selectedBook);
        }
        
        // Restore expanded accordion if it's valid
        if (savedState.expandedAccordion && savedState.expandedAccordion !== false) {
          setExpandedAccordion(savedState.expandedAccordion);
        }
      }
    };

    // Load saved state after booksWithScores is available
    if (booksWithScores.length > 0) {
      loadSavedState();
    }
  }, [booksWithScores, uiStore]);

  // Save state to uiStore when it changes
  useEffect(() => {
    if (selectedBookName && uiStore) {
      uiStore.saveNavigationState(selectedBookName, expandedAccordion);
    }
  }, [selectedBookName, expandedAccordion, uiStore]);

  // Clear saved state when navigating away from personal area
  const clearSavedState = () => {
    if (uiStore) {
      uiStore.clearNavigationState();
    }
  };

  // Cleanup function to clear state when component unmounts
  useEffect(() => {
    return () => {
      // Don't clear state on unmount - we want to keep it for when user returns
    };
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      try {
        await validateToken();
        const { studentData: incomingStudentData } = location.state || {};
        
        // Check if we're on a student page or teacher page
        const isStudentAreaRoute = location.pathname === '/student-area';

        // If we have student data and we're on a student route, use it
        if (incomingStudentData && isStudentAreaRoute) {
          setIsTeacher(true);
          setStudentData(incomingStudentData);
          setSelectedBookName(incomingStudentData[0]?.book_name_en || '');
        } else {
          // If no student data or we're on personal-area, use teacher data
          setIsTeacher(false);
          setStudentData(null);
          
          if (!userData) {
            await getUserData();
          }
          
          // Check if user is guest - guest users cannot access personal area
          const GUEST_USER_ID = 544;
          if (userData?.personalInfo?.id === GUEST_USER_ID) {
            navigate('/home');
            return;
          }
          
          // If teacher has no data at all, redirect to home
          if (!userData?.scoreInfo || userData.scoreInfo.length === 0) {
            navigate('/');
            return;
          }
          
          // Select the first available book
          const userBooks: IUserScoreInfo[] = userData?.scoreInfo || [];
          if (userBooks.length > 0) {
            setSelectedBookName(userBooks[0].book_name_en);
          }
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to initialize data:', error);
        navigate('/');
      }
    };
    initializeData();
  }, [location.state, location.pathname, userData, getUserData, navigate]);

  // Event handlers
  const handleBack = () => {
    const isStudentAreaRoute = location.pathname === '/student-area';
    
    // Clear saved state when navigating away from personal area
    clearSavedState();
    
    navigate(isStudentAreaRoute && location.state?.studentData ? '/classes-management' : '/home');
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const handleBookNameChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedBookName(newValue);
    // Reset expanded accordion when changing books
    setExpandedAccordion(false);
  };

  // Loading state
  if (isLoading) {
    return <LoaderComponent />;
  }

  // If no data after loading and not a teacher
  if (!booksWithScores.length && !isTeacher) {
    return null;
  }

  // User name display
  const userName = studentData
    ? location.state?.studentName || 'תלמיד/ה'
    : userData?.personalInfo?.name || '';

  // Selected book score data
  const selectedBookScore = booksWithScores.find(
    (book: IUserScoreInfo) => book.book_name_en === selectedBookName
  )?.book_statistics;

  // Get display name for book
  const getBookDisplayName = (bookName: string): string => {
    const found = BOOK_LIST.find((book: IBook) => book.name_en === bookName);
    if (found && found.name_he) return found.name_he;
    const fromScore = booksWithScores.find(
      (book: IUserScoreInfo) => book.book_name_en === bookName
    );
    return fromScore?.book_name_he || bookName;
  };

  // Render
  return (
    <main className={`body my-reading-page ${isTeacher ? 'student-area-view' : 'teacher-area-view'}`}>
      <section className="head-section">
        <div className="headers">
          <div className="navigate">
            <ArrowRight onClick={handleBack} />
          </div>
          <h1>
            {isTeacher && (
              <FaceRounded style={{ 
                marginInlineEnd: '6px',
                fontSize: '42px',
                verticalAlign: 'middle'
              }} />
            )}
            {userName && isTeacher ? userName : t('personalArea')}
          </h1>
        </div>
        
        {booksWithScores.length > 1 && (
          <div className="book-type-tabs">
            <Tabs
              value={selectedBookName}
              onChange={handleBookNameChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {booksWithScores.map((book: IUserScoreInfo) => (
                <Tab
                  key={book.book_name_en}
                  value={book.book_name_en}
                  label={getBookDisplayName(book.book_name_en)}
                />
              ))}
            </Tabs>
          </div>
        )}
        
        {!booksWithScores.length ? (
          <div className="summary-table no-data-message">
            {t('newStudentMessage')}
          </div>
        ) : (
          <div className="summary-table">
            <div className="grid-item">{t('sumOfMemorized')}</div>
            <div className="grid-item summary-value">
              <b>{selectedBookScore?.total_by_heart || 0}</b>
            </div>
            <div className="grid-item">{t('sumOfNotMemorized')}</div>
            <div className="grid-item summary-value">
              <b>{selectedBookScore?.total_reading || 0}</b>
            </div>
          </div>
        )}
      </section>
      
      <section className="levelOne-accordions">
        {/* Use the custom hook to get accordion items - no more complex logic here! */}
        {accordionItems.map((item, index) => {
          // Generate panel ID for this accordion
          const panelId = `panel${index}`;
          // Check if this accordion should be expanded
          const isExpanded = expandedAccordion === panelId;
          
          return (
            <BookAccordionRouter
              key={item.key}
              bookName={item.bookName}
              levelOneData={item.levelOneData}
              expanded={isExpanded}
              onChange={handleAccordionChange(panelId)}
              isTeacher={item.isTeacher}
            />
          );
        })}
      </section>
    </main>
  );
});

export default PersonalArea;
